import {
  getRouteTrackingEnvironment,
  IdoFunnelEventName,
  routeTrackingConfig,
  RouteTrackingConversionEvent,
  RouteTrackingRule,
  RouteTrackingTarget,
} from "app/config/route-tracking-config";

export type TrackingPage =
  | {
      type: "ido";
      id: string;
    }
  | {
      type: "quest";
      id: string;
    }
  | {
      type: "build_karma";
      id: string;
    };

const normalizeTrackingId = (value: string): string => {
  const trimmedValue = value.trim();

  try {
    return decodeURIComponent(trimmedValue);
  } catch (error) {
    return trimmedValue;
  }
};

const isValidDate = (date: Date): boolean => !isNaN(date.getTime());

const warnInvalidTrackingRule = (rule: RouteTrackingRule, field: string) => {
  if (process.env.NODE_ENV === "production") {
    return;
  }

  // eslint-disable-next-line no-console
  console.warn(`Route tracking rule "${rule.id}" has invalid ${field}`);
};

const isRuleInActiveWindow = (rule: RouteTrackingRule, now: Date): boolean => {
  const activeUntil = new Date(rule.activeUntil);

  if (!isValidDate(activeUntil)) {
    warnInvalidTrackingRule(rule, "activeUntil");
    return false;
  }

  if (now > activeUntil) {
    return false;
  }

  if (!rule.activeFrom) {
    return true;
  }

  const activeFrom = new Date(rule.activeFrom);

  if (!isValidDate(activeFrom)) {
    warnInvalidTrackingRule(rule, "activeFrom");
    return false;
  }

  return now >= activeFrom;
};

const matchesTarget = (
  target: RouteTrackingTarget,
  page: TrackingPage
): boolean => {
  if (target.type === "build_karma") {
    return page.type === "build_karma" || page.type === "quest";
  }

  return (
    target.type === page.type &&
    target.ids.some(
      (id) => normalizeTrackingId(id) === normalizeTrackingId(page.id)
    )
  );
};

export const getActiveRouteTrackingRules = (
  page: TrackingPage | null,
  now = new Date()
): RouteTrackingRule[] => {
  if (!page) {
    return [];
  }

  const environment = getRouteTrackingEnvironment();

  return routeTrackingConfig[environment].filter(
    (rule) =>
      rule.enabled &&
      isRuleInActiveWindow(rule, now) &&
      rule.targets.some((target) => matchesTarget(target, page))
  );
};

export const getRouteTrackingConversionEvent = (
  page: TrackingPage | null,
  funnelEvent: IdoFunnelEventName,
  provider: RouteTrackingRule["provider"] = "x",
  now = new Date()
): RouteTrackingConversionEvent | null => {
  const rules = getActiveRouteTrackingRules(page, now).filter(
    (rule) => rule.provider === provider
  );

  for (const rule of rules) {
    const conversionEvent = rule.conversionEvents?.find(
      (event) => event.funnelEvent === funnelEvent
    );

    if (conversionEvent) {
      if (rule.provider === "meta" && rule.pixelId) {
        return {
          ...conversionEvent,
          pixelId: rule.pixelId,
        };
      }

      return conversionEvent;
    }
  }

  return null;
};
